import {
  Group,
  Button,
  Menu,
  Text,
  Stack,
  NavLink,
  Divider,
} from "@mantine/core";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IconLayoutDashboard,
  IconUsers,
  IconShieldLock,
  IconSettings2,
  IconChevronDown,
  IconShieldCog,
  IconKey,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { modulesApi } from "../api/modules.api";
import { getModuleIcon } from "../modules/registry";
import { useAuth } from "../auth/AuthContext";

function isPathActive(pathname, to) {
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(to + "/");
}

/**
 * Horizontal top navigation. Used inside the AppShell header on desktop.
 * Compact button styling with an underline on the active route.
 */
export function TopNav() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { data: modules = [] } = useQuery({
    queryKey: ["modules"],
    queryFn: modulesApi.list,
  });

  const adminActive =
    pathname.startsWith("/admin/users") ||
    pathname.startsWith("/admin/roles") ||
    pathname.startsWith("/admin/module-configs") ||
    pathname.startsWith("/admin/api-keys");

  return (
    <Group gap={4} wrap="nowrap">
      <NavButton
        to="/"
        icon={<IconLayoutDashboard size={16} />}
        label="Dashboard"
        active={isPathActive(pathname, "/")}
        onClick={() => navigate("/")}
      />

      {modules.map((m) => {
        const Icon = getModuleIcon(m.icon);
        return (
          <NavButton
            key={m.key}
            to={m.path}
            icon={<Icon size={16} />}
            label={m.name}
            active={isPathActive(pathname, m.path)}
            onClick={() => navigate(m.path)}
          />
        );
      })}

      {user?.isAdmin && (
        <Menu shadow="md" width={240} position="bottom-start">
          <Menu.Target>
            <Button
              variant={adminActive ? "light" : "subtle"}
              color={adminActive ? "indigo" : "gray"}
              size="sm"
              leftSection={<IconShieldCog size={16} />}
              rightSection={<IconChevronDown size={14} />}
              styles={{
                root: { fontWeight: 500 },
              }}
            >
              Administration
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>Manage</Menu.Label>
            <Menu.Item
              leftSection={<IconUsers size={14} />}
              onClick={() => navigate("/admin/users")}
            >
              Users
            </Menu.Item>
            <Menu.Item
              leftSection={<IconShieldLock size={14} />}
              onClick={() => navigate("/admin/roles")}
            >
              Roles &amp; Permissions
            </Menu.Item>
            <Menu.Item
              leftSection={<IconSettings2 size={14} />}
              onClick={() => navigate("/admin/module-configs")}
            >
              Module Configuration
            </Menu.Item>
            <Menu.Item
              leftSection={<IconKey size={14} />}
              onClick={() => navigate("/admin/api-keys")}
            >
              API Keys
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      )}
    </Group>
  );
}

function NavButton({ icon, label, active, onClick }) {
  return (
    <Button
      variant={active ? "light" : "subtle"}
      color={active ? "indigo" : "gray"}
      size="sm"
      leftSection={icon}
      onClick={onClick}
      styles={{
        root: {
          fontWeight: 500,
        },
      }}
    >
      {label}
    </Button>
  );
}

/**
 * Vertical version of the nav, used inside the mobile Drawer.
 */
export function MobileNav({ onNavigate }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { data: modules = [] } = useQuery({
    queryKey: ["modules"],
    queryFn: modulesApi.list,
  });

  const go = (to) => {
    navigate(to);
    onNavigate?.();
  };

  return (
    <Stack gap={2} p="sm">
      <NavLink
        label="Dashboard"
        leftSection={<IconLayoutDashboard size={18} />}
        active={isPathActive(pathname, "/")}
        onClick={() => go("/")}
      />

      <Divider my="xs" label="Modules" labelPosition="left" />
      {modules.length === 0 && (
        <Text size="xs" c="dimmed" px="sm">
          No modules available
        </Text>
      )}
      {modules.map((m) => {
        const Icon = getModuleIcon(m.icon);
        return (
          <NavLink
            key={m.key}
            label={m.name}
            description={m.description}
            leftSection={<Icon size={18} />}
            active={isPathActive(pathname, m.path)}
            onClick={() => go(m.path)}
          />
        );
      })}

      {user?.isAdmin && (
        <>
          <Divider my="xs" label="Administration" labelPosition="left" />
          <NavLink
            label="Users"
            leftSection={<IconUsers size={18} />}
            active={isPathActive(pathname, "/admin/users")}
            onClick={() => go("/admin/users")}
          />
          <NavLink
            label="Roles & Permissions"
            leftSection={<IconShieldLock size={18} />}
            active={isPathActive(pathname, "/admin/roles")}
            onClick={() => go("/admin/roles")}
          />
          <NavLink
            label="Module Configuration"
            leftSection={<IconSettings2 size={18} />}
            active={isPathActive(pathname, "/admin/module-configs")}
            onClick={() => go("/admin/module-configs")}
          />
          <NavLink
            label="API Keys"
            leftSection={<IconKey size={18} />}
            active={isPathActive(pathname, "/admin/api-keys")}
            onClick={() => go("/admin/api-keys")}
          />
        </>
      )}
    </Stack>
  );
}
